import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, Loader2, MapPin, User, CalendarPlus, Copy, ExternalLink, Check, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { API, BACKEND_URL, isWeddingJob } from "@/utils/constants";

export const MyJobsDialog = ({ open, onClose, teamMembers, onOpenJobSheet }) => {
  const [selectedMember, setSelectedMember] = useState("");
  const [myJobs, setMyJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  const fetchMyJobs = async (memberId) => {
    if (!memberId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/my-jobs/${memberId}`);
      setMyJobs(response.data.jobs);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleMemberChange = (memberId) => {
    setSelectedMember(memberId);
    fetchMyJobs(memberId);
  };

  const getCalendarUrl = () => {
    return `${BACKEND_URL}/api/calendar/${selectedMember}.ics`;
  };

  const getGoogleCalendarSubscribeUrl = () => {
    const webcalUrl = getCalendarUrl().replace(/^https?:\/\//, 'webcal://');
    return `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`;
  };

  const getFullCalendarUrl = () => `${BACKEND_URL}/api/calendar/all.ics`;

  const getFullGoogleCalendarUrl = () => {
    const webcalUrl = getFullCalendarUrl().replace(/^https?:\/\//, 'webcal://');
    return `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`;
  };

  const copyFullCalendarUrl = () => {
    navigator.clipboard.writeText(getFullCalendarUrl());
    setCopiedAll(true);
    toast.success('Full calendar URL copied!');
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const copyCalendarUrl = () => {
    navigator.clipboard.writeText(getCalendarUrl());
    setCopied(true);
    toast.success('Calendar URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  };

  const selectedMemberName = teamMembers.find(m => m.id === selectedMember)?.name || '';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-heading text-xl text-[#605e5e] flex items-center gap-2">
            <User className="h-5 w-5" />
            My Jobs
          </DialogTitle>
          <DialogDescription className="text-sm text-[#8d8b8b]">
            View your assigned jobs and sync to your calendar
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-shrink-0 space-y-4">
          {/* Full team calendar */}
          <div className="bg-[#f9fafb] rounded-lg p-3 space-y-2" data-testid="full-calendar-section">
            <div className="flex items-center gap-2 text-[#605e5e] font-medium text-sm">
              <CalendarPlus className="h-4 w-4" />
              Full Team Calendar
            </div>
            <p className="text-xs text-[#8d8b8b]">
              Subscribe to every job on the rota (all team members).
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={copyFullCalendarUrl}
                className="flex-1 text-xs h-8"
                data-testid="copy-full-calendar-url-btn"
              >
                {copiedAll ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                {copiedAll ? 'Copied!' : 'Copy URL'}
              </Button>
              <Button
                size="sm"
                onClick={() => window.open(getFullGoogleCalendarUrl(), '_blank')}
                className="flex-1 bg-[#605e5e] hover:bg-[#4b4a4a] text-xs h-8"
                data-testid="add-full-calendar-google-btn"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Google Calendar
              </Button>
            </div>
          </div>

          {/* Team member selector */}
          <div className="space-y-2">
            <Label className="text-[#605e5e]">Select your name</Label>
            <Select value={selectedMember} onValueChange={handleMemberChange}>
              <SelectTrigger data-testid="my-jobs-member-select">
                <SelectValue placeholder="Choose team member..." />
              </SelectTrigger>
              <SelectContent>
                {teamMembers.map(member => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedMember && (
            <div className="bg-[#f9fafb] rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-[#605e5e] font-medium text-sm">
                <CalendarPlus className="h-4 w-4" />
                Sync My Jobs to Calendar
              </div>
              <p className="text-xs text-[#8d8b8b]">
                Subscribe to just your assigned jobs — updates automatically.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyCalendarUrl}
                  className="flex-1 text-xs h-8"
                  data-testid="copy-calendar-url-btn"
                >
                  {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                  {copied ? 'Copied!' : 'Copy URL'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => window.open(getGoogleCalendarSubscribeUrl(), '_blank')}
                  className="flex-1 bg-[#605e5e] hover:bg-[#4b4a4a] text-xs h-8"
                  data-testid="add-to-google-calendar-btn"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Google Calendar
                </Button>
              </div>
            </div>
          )}
        </div>

        {selectedMember && (
          <>
            <Separator className="flex-shrink-0" />
            
            <div className="flex items-center justify-between flex-shrink-0">
              <span className="text-sm font-medium text-[#605e5e]">
                {selectedMemberName}'s Jobs
              </span>
              <Badge variant="outline" className="text-xs">{myJobs.length}</Badge>
            </div>

            {/* Scrollable job list */}
            <div className="overflow-y-auto max-h-[35vh] pr-1" data-testid="my-jobs-list">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#605e5e]" />
                </div>
              ) : myJobs.length === 0 ? (
                <div className="text-center py-8 text-[#8d8b8b]">
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No upcoming jobs</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {myJobs.map(job => (
                    <div
                      key={job.id}
                      className="p-3 bg-white border border-[#e5e7eb] rounded-lg"
                      data-testid={`my-job-${job.id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <Badge variant="outline" className="text-xs flex-shrink-0">
                          {job.my_role}
                        </Badge>
                        {job.has_video_booth && (
                          <Badge className="bg-[#605e5e] text-white text-xs flex-shrink-0">
                            Booth
                          </Badge>
                        )}
                      </div>
                      <p className="font-medium text-sm text-[#1f2937] line-clamp-2">
                        {job.event_name || job.title}
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-2 text-xs text-[#6b7280]">
                          <span className="flex items-center gap-1 flex-shrink-0">
                            <Calendar className="h-3 w-3" />
                            {formatDate(job.date)}
                          </span>
                          {job.location && (
                            <span className="flex items-center gap-1 truncate">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{job.location.split(',')[0]}</span>
                            </span>
                          )}
                        </div>
                        {isWeddingJob(job) && onOpenJobSheet && (
                          <button
                            onClick={() => onOpenJobSheet(job.id)}
                            className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-medium bg-[#fcd34d] text-black hover:bg-[#fbbf24] px-1.5 py-0.5 rounded transition-colors"
                            data-testid={`my-jobs-prep-sheet-btn-${job.id}`}
                          >
                            <FileText className="h-3 w-3" />
                            Prep Sheet
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        
        <DialogFooter className="flex-shrink-0 mt-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto" data-testid="close-my-jobs-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
